import React, { useEffect } from 'react'
import { Grid, Container, Button, Box, Paper } from '@material-ui/core'
import { useNavigate } from 'react-router'
import { motion, useAnimation } from 'framer-motion'
import { useInView } from 'react-intersection-observer'
import { backgroundStyle } from '../styles/background'
import { useSelector } from 'react-redux'
import { State } from '../types/state'
import { typographyStyles } from '../styles/typography'
import { featureCard, sectionDivider } from '../styles/modern'
import SecurityIcon from '@mui/icons-material/Security'
import AutorenewIcon from '@mui/icons-material/Autorenew'
import EmojiObjectsIcon from '@mui/icons-material/EmojiObjects'

const LandingPage = (): JSX.Element => {
  const navigate = useNavigate()
  const { username } = useSelector((state: State) => state.user)
  const firstControls = useAnimation()
  const secondControls = useAnimation()
  const thirdControls = useAnimation()

  const [firstSectionRef, firstSectionInView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  })
  const [secondSectionRef, secondSectionInView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  })
  const [thirdSectionRef, thirdSectionInView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  })

  useEffect(() => {
    if (firstSectionInView) {
      void firstControls.start({ opacity: 1, y: 0 })
    }
  }, [firstControls, firstSectionInView])

  useEffect(() => {
    if (secondSectionInView) {
      void secondControls.start({ opacity: 1, y: 0 })
    }
  }, [secondControls, secondSectionInView])

  useEffect(() => {
    if (thirdSectionInView) {
      void thirdControls.start({ opacity: 1, y: 0 })
    }
  }, [thirdControls, thirdSectionInView])

  return (
    <>
      <Container
        style={{
          textAlign: 'center',
          paddingTop: '15vh',
          paddingBottom: '10vh',
          color: '#2d3748'
        }}
      >
        <div style={backgroundStyle} />
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6 }}
        >
          <h1 style={{ ...typographyStyles.h1, marginBottom: '24px' }}>
            Welcome to VReMa!
          </h1>
        </motion.div>
        <motion.div
          ref={firstSectionRef}
          initial={{ opacity: 0, y: 50 }}
          animate={firstControls}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <h6 style={{ ...typographyStyles.h6, fontSize: '1.5rem', maxWidth: '800px', margin: '0 auto' }}>
            The easiest and most intuitive program for generating VAT receipts.
          </h6>
        </motion.div>
        {username === null && (
          <>
            <div style={sectionDivider} />
            <h2 style={{ ...typographyStyles.h2, marginBottom: '60px' }}>
              Features of Our Application
            </h2>
            <Grid container spacing={4} justify='center' style={{ marginBottom: '40px' }}>
              <Grid item xs={12} md={4}>
                <motion.div
                  ref={secondSectionRef}
                  initial={{ opacity: 0, y: 50 }}
                  animate={secondControls}
                  transition={{ duration: 0.5 }}
                  whileHover={{ y: -8 }}
                >
                  <Paper elevation={0} style={featureCard} className='modern-card'>
                    <Box
                      style={{
                        width: '70px',
                        height: '70px',
                        borderRadius: '50%',
                        backgroundImage: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 24px',
                        boxShadow: '0 10px 25px rgba(102, 126, 234, 0.3)'
                      }}
                    >
                      <EmojiObjectsIcon style={{ fontSize: 35, color: 'white' }} />
                    </Box>
                    <h5 style={{ ...typographyStyles.h5, marginBottom: '16px' }}>
                      User-Friendly
                    </h5>
                    <p style={typographyStyles.body1}>
                      An intuitive interface makes invoice creation a breeze.
                    </p>
                  </Paper>
                </motion.div>
              </Grid>
              <Grid item xs={12} md={4}>
                <motion.div
                  ref={secondSectionRef}
                  initial={{ opacity: 0, y: 50 }}
                  animate={secondControls}
                  transition={{ duration: 0.5, delay: 0.2 }}
                  whileHover={{ y: -8 }}
                >
                  <Paper elevation={0} style={featureCard} className='modern-card'>
                    <Box
                      style={{
                        width: '70px',
                        height: '70px',
                        borderRadius: '50%',
                        backgroundImage: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 24px',
                        boxShadow: '0 10px 25px rgba(102, 126, 234, 0.3)'
                      }}
                    >
                      <AutorenewIcon style={{ fontSize: 35, color: 'white' }} />
                    </Box>
                    <h5 style={{ ...typographyStyles.h5, marginBottom: '16px' }}>
                      Automatic Updates
                    </h5>
                    <p style={typographyStyles.body1}>
                      Always use the latest VAT rates and receipts templates.
                    </p>
                  </Paper>
                </motion.div>
              </Grid>
              <Grid item xs={12} md={4}>
                <motion.div
                  ref={secondSectionRef}
                  initial={{ opacity: 0, y: 50 }}
                  animate={secondControls}
                  transition={{ duration: 0.5, delay: 0.4 }}
                  whileHover={{ y: -8 }}
                >
                  <Paper elevation={0} style={featureCard} className='modern-card'>
                    <Box
                      style={{
                        width: '70px',
                        height: '70px',
                        borderRadius: '50%',
                        backgroundImage: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 24px',
                        boxShadow: '0 10px 25px rgba(102, 126, 234, 0.3)'
                      }}
                    >
                      <SecurityIcon style={{ fontSize: 35, color: 'white' }} />
                    </Box>
                    <h5 style={{ ...typographyStyles.h5, marginBottom: '16px' }}>
                      Data Security
                    </h5>
                    <p style={typographyStyles.body1}>
                      Your data is secure with us, using the latest security
                      technologies.
                    </p>
                  </Paper>
                </motion.div>
              </Grid>
            </Grid>
            <div style={sectionDivider} />
            <h2 style={{ ...typographyStyles.h2, marginBottom: '32px' }}>
              See for Yourself!
            </h2>
            <motion.div
              ref={thirdSectionRef}
              initial={{ opacity: 0, y: 50 }}
              animate={thirdControls}
              transition={{ duration: 0.5 }}
            >
              <p style={{ ...typographyStyles.body1, maxWidth: '700px', margin: '0 auto 40px' }}>
                Join thousands of satisfied VReMa users and start generating
                receipts easily and pleasantly.
              </p>
              <Button
                variant='contained'
                color='primary'
                style={{
                  marginTop: '20px',
                  marginBottom: '40px',
                  padding: '16px 48px',
                  fontSize: '1.125rem',
                  fontWeight: 600,
                  borderRadius: '50px',
                  textTransform: 'none',
                  backgroundImage: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  boxShadow: '0 10px 25px rgba(102, 126, 234, 0.4)',
                  transition: 'all 0.3s ease'
                }}
                onClick={() => {
                  navigate('/register')
                }}
              >
                Get Started Now
              </Button>
            </motion.div>
          </>
        )}
        {/* {username !== null && (
          <>
            <Divider style={{ margin: '10vw' }} />
            <h2 style={typographyStyles.h2}>
              See how many receipts you have generated
            </h2>
            <motion.div
              ref={thirdSectionRef}
              initial={{ opacity: 0, y: 50 }}
              animate={thirdControls}
              transition={{ duration: 0.5 }}
            >
              <Chart />
            </motion.div>
          </>
        )} */}
      </Container>
    </>
  )
}

export default LandingPage
